import { type IPartner, Partner } from '@app/purchase/partner/models/Partner';
import { useRoute } from '@shared/composables/useRoute';
import { useFetchListData } from '@shared/composables/useFetchListData';
import type { FormOptions } from '@shared/types/Form.ts';
import { useInjectErrorState } from '@shared/composables/useProvideErrorState.ts';
import { useForm } from '@shared/composables/useForm.ts';
import { useWithValidationConfig } from '@shared/composables/useWithValidationConfig.ts';
import { usePayload } from '@shared/composables/usePayload.ts';

export function usePartnerRepository<T extends IPartner = IPartner>() {
    const command = () => {
        const store = (data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                Partner.routes().store,
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute };
        };

        const update = (id: string, data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                {
                    url: useRoute().build(Partner.routes().update, {
                        id,
                    }),
                    method: Partner.routes().update.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute };
        };

        const destroy = <U extends { id: string }>(id: string, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<U>({ id } as U).precognition(
                {
                    url: useRoute().build(Partner.routes().destroy, {
                        id,
                    }),
                    method: Partner.routes().destroy.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.submit(options);
            }

            return { form, execute };
        };

        return {
            store,
            update,
            destroy,
        };
    };

    const query = () => {
        const show = (id: string) => {
            return { endpoint: useRoute().build(Partner.routes().show, { id }) };
        };

        const list = () => {
            const { data: partners, processing: fetchingPartners } = useFetchListData<Partner>(useRoute().build(Partner.routes().index), 'appSection@partner::IndexPartnersPage', (payload) => Partner.from(payload));
            return { partners, fetchingPartners };
        };

        return {
            show,
            list,
        };
    };

    return {
        command: command(),
        query: query(),
    };
}
